import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lightbulb, Users, Package, ChevronLeft, ChevronRight, LogOut, Settings, User, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { SystemInfo } from '../types/auth-types';

type NavigationItem = {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
};

export type NavigationGuardCheck = (path: string) => boolean;

interface SidebarProps {
  onNavigationGuard?: NavigationGuardCheck;
  onLogout?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onNavigationGuard, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user, systemInfo, isAdmin } = useAuth();

  const navigationItems: NavigationItem[] = [
    { id: 'skills', label: 'Skills', icon: Lightbulb, path: '/marketplace/skills' },
    { id: 'agents', label: 'Agents', icon: Users, path: '/marketplace/agents' },
    { id: 'packages', label: 'Packages', icon: Package, path: '/marketplace/packages' },
    { id: 'executions', label: 'Executions', icon: FileText, path: '/marketplace/executions' }
  ];

  const isActivePath = (path: string): boolean => {
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string) => {
    if (onNavigationGuard) {
      if (!onNavigationGuard(path)) {
        return;
      }
    }
    navigate(path);
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <div className={`bg-gray-800 text-white h-screen transition-all duration-300 relative flex flex-col
      ${isCollapsed ? 'w-16' : 'w-64'}`}>
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-4 top-8 bg-white text-gray-800 p-1.5 rounded-full 
          shadow-lg hover:bg-gray-100 border border-gray-200 
          transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      <div className={`p-4 ${isCollapsed ? 'px-2' : ''} flex-grow`}>
        {/* User Profile Section */}
        <div className={`mb-8 overflow-hidden ${isCollapsed ? 'text-center' : ''}`}>
          <h1 className={`font-bold transition-all duration-300 ${isCollapsed ? 'text-sm' : 'text-xl'}`}>
            {isCollapsed ? '' : 'Your Marketplace'}
          </h1>
          
          <div className={`mt-4 flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
  <div className="bg-gray-700 rounded-full p-1 overflow-hidden flex items-center justify-center" 
       style={{ width: isCollapsed ? '36px' : '40px', height: isCollapsed ? '36px' : '40px' }}>
    {user?.profileImage ? (
      <img 
        src={user.profileImage}
        alt={`${user.firstName}'s profile`}
        className="w-full h-full rounded-full object-cover"
        onError={(e) => {
          // Hide the image and show the fallback icon
          e.currentTarget.style.display = 'none';
          const parent = e.currentTarget.parentElement;
          if (parent) {
            const fallbackIcon = parent.querySelector('.fallback-icon');
            if (fallbackIcon) {
              (fallbackIcon as HTMLElement).style.display = 'block';
            }
          }
        }}
      />
    ) : null}
    <User 
      size={isCollapsed ? 20 : 24} 
      className="text-gray-300 fallback-icon" 
      style={{display: user?.profileImage ? 'none' : 'block'}}
    />
  </div>
  {!isCollapsed && user?.email && (
    <div className="ml-3 overflow-hidden">
      <p className="text-sm text-gray-300 truncate" title={user.firstName + ' ' + user.lastName}>
        {user.firstName} {user.lastName}
              </p>
            </div>
          )}
        </div>
        </div>
        
        <nav className="space-y-2">
          {navigationItems.map(({ id, label, icon: Icon, path }, index) => (
            <React.Fragment key={id}>
              {id === 'executions' && (
                <div className={`my-2 border-t border-gray-700 ${isCollapsed ? 'mx-2' : 'mx-4'}`}></div>
              )}
              <button
                onClick={() => handleNavigation(path)}
                className={`w-full flex items-center rounded-lg transition-all duration-200
                  ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-4 py-3 space-x-3'}
                  ${isActivePath(path)
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-300 hover:bg-gray-700'}`}
                title={isCollapsed ? label : undefined}
              >
                <Icon size={20} />
                {!isCollapsed && <span>{label}</span>}
              </button>
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Bottom section with Admin and Logout */}
      <div className={`p-4 ${isCollapsed ? 'px-2' : ''}`}>
        {isAdmin && (
          <button
            onClick={() => handleNavigation('/marketplace/admin')}
            className={`w-full flex items-center rounded-lg transition-all duration-200 mb-4
              ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-4 py-3 space-x-3'}
              ${isActivePath('/marketplace/admin') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
            title={isCollapsed ? 'Admin' : undefined}
          >
            <Settings size={20} />
            {!isCollapsed && <span>Admin</span>}
          </button>
        )}

        <div className="border-t border-gray-700 pt-4">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center rounded-lg transition-all duration-200
              ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-4 py-3 space-x-3'}
              text-gray-300 hover:bg-gray-700`}
            title={isCollapsed ? 'Logout' : undefined}
          >
            <LogOut size={20} />
            {!isCollapsed && <span>Log Out</span>}
          </button>

          {systemInfo && (
            <div className={`mt-2 py-2 text-sm font-medium text-gray-400 text-center border-t border-gray-700
              ${isCollapsed ? 'px-1' : 'px-2'}`}>
              {isCollapsed ? '' : `Version ${systemInfo.version}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;